import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import intl from 'components/i18n/ReactIntlWrapper';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import WorkflowActionsBar from '../Workflow/WorkflowActionsBar';
import { WorkflowProvider } from 'components/Workflow/WorkflowContext';
import { formatDateInvoices } from '../../common/utils';
import { WorkflowStatusChip } from '../common/WorkflowStatusChip';
import InvoiceReconciliationDialog from '../Invoicing/InvoiceReconciliationDialog';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { hasPermission } from 'components/authorization/PermissionUtils';
import { SCOPES } from 'common/permissions';

const PAID_STATE_ID = 118;

const InvoicesListAccountsPayable = ({
  handleSave,
  searchResult,
  isValidating,
  loading,
  setLoading,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  WorkflowModuleId
}) => {
  const navigate = useNavigate();
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const page = paginationModel?.page ?? 0;
  const pageSize = paginationModel?.pageSize ?? 10;
  const canDeleteAP = hasPermission({ scopes: [SCOPES['accountspayable.edit']] });

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteClick = (row: any) => {
    setDeleteRow(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRow?.jobId) return;
    setDeleteLoading(true);
    try {
      await api.invoiceApiWrapper.deleteApInvoice('1', deleteRow.jobId, '');
      setDeleteDialogOpen(false);
      setDeleteRow(null);
      handleSave();
    } catch (error) {
      handleAPIError(error, toast, {});
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteRow(null);
  };

  // AP reconciliation dialog state
  const [apReconcileOpen, setApReconcileOpen] = useState(false);
  const [apReconcileRow, setApReconcileRow] = useState<any>(null);
  const pendingApResolve = useRef<((result: boolean | 'handled') => void) | null>(null);

  const handleBeforeAction = useCallback((row: any) => (targetStateId: number): Promise<boolean | 'handled'> => {
    if (targetStateId === PAID_STATE_ID) {
      setApReconcileRow(row);
      setApReconcileOpen(true);
      return new Promise<boolean | 'handled'>((resolve) => {
        pendingApResolve.current = resolve;
      });
    }
    return Promise.resolve(true);
  }, []);

  const handleApReconcileConfirm = (fullyReconciled: boolean) => {
    setApReconcileOpen(false);
    setApReconcileRow(null);
    if (pendingApResolve.current) {
      pendingApResolve.current(fullyReconciled ? true : false);
      pendingApResolve.current = null;
    }
  };

  const handleApReconcileCancel = () => {
    setApReconcileOpen(false);
    setApReconcileRow(null);
    if (pendingApResolve.current) {
      pendingApResolve.current(false);
      pendingApResolve.current = null;
    }
  };

  const rows = (searchResult?.queryResult || []).map((row, index) => ({
    ...row,
    __gridRowId: `${page}-${pageSize}-${row?.id ?? 'no-id'}-${row?.jobId ?? 'no-job'}-${index}`,
  }));

  const columns = [];

  const handleInvoiceDetail = (invoiceID, jobId) => {
    if (isValidating) {
      navigate(`/accountspayable/validate/${jobId}`);
    } else {
      navigate(`/invoice/detail/${invoiceID}`);
    }
  };

  columns.push(
    {
      field: 'date',
      headerName: intl.get('invoicesList.date'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{formatDateInvoices(params.row.date)}</>,
    },
    {
      field: 'number',
      headerName: intl.get('invoicesList.invoice.number'),
      width: 250,
      flex: 1,
    },
    {
      field: 'amount',
      headerName: intl.get('invoicesList.amount'),
      width: 250,
      flex: 1,
    },
    {
      field: 'status',
      headerName: intl.get('invoicesList.status'),
      width: 250,
      flex: 1,
      renderCell: (params) => (
        <WorkflowStatusChip stateId={params.row.idWorkflowState} />
      ),
    }
  );

  columns.unshift({
    field: 'name',
    headerName: intl.get('invoicesList.name'),
    width: 250,
    flex: 1,
    valueGetter: (value, row) => {
      return isValidating ? row.senderName : row.from;
    },
    renderCell: (params) => (
      <>
        {isValidating
          ? params.row.senderName === null || params.row.senderName === ''
            ? '-'
            : params.row.senderName
          : params.row.from === null || params.row.from === ''
            ? '-'
            : params.row.from}
      </>
    ),
  });

  if (!isValidating) {
    columns.push({
      field: 'id',
      headerName: intl.get('invoicesList.invoice.id'),
      width: 250,
      flex: 1,
    });

    const handleSaveClick = () => {
      setLoading(true);
      setTimeout(() => {
        handleSave();
        setLoading(false);
      }, 2000);
    };

    columns.push({
      field: 'workflowActions',
      headerName: intl.get('invoicesList.actions'),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <WorkflowActionsBar
          viewMode="icons"
          onActionClick={handleSaveClick}
          id={params.row.id}
          stateId={params.row.idWorkflowState}
          loading={loading}
          onBeforeAction={handleBeforeAction(params.row)}
        />
      ),
    });
  }

  columns.push({
    field: 'actions',
    headerName: intl.get('invoicesList.detail'),
    width: 150,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" justifyContent="center">
        <IconButton onClick={() => handleInvoiceDetail(params.row.id, params.row.jobId)}>
          <VisibilityIcon />
        </IconButton>
        {isValidating && canDeleteAP && params.row.jobId && (
          <IconButton onClick={() => handleDeleteClick(params.row)} color="error" size="small">
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    ),
  });

  return (
    <>
        <Box style={{ width: '100%', display: 'flex' }}>
          <WorkflowProvider moduleId={WorkflowModuleId}>
            <DataGrid
              rows={rows}
              getRowId={(row) => row.__gridRowId}
              columns={columns}
              loading={loading}
              rowCount={searchResult?.totalRecords || 0}
              paginationModel={paginationModel}
              onPaginationModelChange={onPaginationModelChange}
              sortModel={sortModel}
              onSortModelChange={onSortModelChange}
              pageSizeOptions={[5, 10, 20]}
              paginationMode="server"
              sortingMode="server"
            />
          </WorkflowProvider>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete this invoice from the queue? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceReconciliationDialog
        open={apReconcileOpen}
        invoiceId={apReconcileRow?.id ?? 0}
        invoiceAmount={Math.round((apReconcileRow?.amount ?? 0) * 100)}
        currency={apReconcileRow?.currency?.code ?? ''}
        incoming={0}
        onConfirm={handleApReconcileConfirm}
        onCancel={handleApReconcileCancel}
      />
    </>
  );
};

export default InvoicesListAccountsPayable;
